import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import { BreadcrumbSchema } from '../components/StructuredData';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data } = await axios.get(`${API}/api/blog?published_only=true`);
      setPosts(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const defaultPosts = [
    {
      id: 'sample-1',
      title: 'Why Daily Bookkeeping Beats Monthly Close',
      slug: 'daily-bookkeeping-vs-monthly',
      excerpt: 'Traditional month-end closes leave founders flying blind. Here\'s why T+1 posting changes everything.',
      category: 'Insights',
      created_at: '2025-01-15',
      cover_image: 'https://images.unsplash.com/photo-1643494824830-1e63d776d6ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYmxhY2slMjBhbmQlMjB3aGl0ZSUyMGRlc2slMjBjb2ZmZWV8ZW58MHx8fHwxNzc1ODAzNjA5fDA&ixlib=rb-4.1.0&q=85',
    },
    {
      id: 'sample-2',
      title: 'GST Compliance for Cross-Border SaaS',
      slug: 'gst-compliance-cross-border',
      excerpt: 'Navigating GST, TDS, and place-of-supply rules when selling globally from India.',
      category: 'Compliance',
      created_at: '2025-01-10',
      cover_image: '',
    },
    {
      id: 'sample-3',
      title: 'Unit Economics Every D2C Founder Should Track',
      slug: 'unit-economics-d2c',
      excerpt: 'LTV, CAC, contribution margin, and AOV - the metrics that separate thriving brands from struggling ones.',
      category: 'FP&A',
      created_at: '2025-01-05',
      cover_image: '',
    },
  ];

  const displayPosts = posts.length > 0 ? posts : defaultPosts;

  return (
    <div>
      <SEO
        title="Blog - Insights & Resources"
        description="Practical guides on bookkeeping, compliance, and financial strategy for growing businesses in India, USA, and UAE."
        path="/blog"
      />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }, { name: 'Blog', path: '/blog' }]} />

      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-6">Blog</p>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-none text-black mb-6">
            Insights & Resources
          </h1>
          <p className="text-base text-gray-600 max-w-2xl">
            Practical guides on bookkeeping, compliance, and financial strategy for growing businesses.
          </p>
        </div>
      </section>

      <section data-testid="blog-listing" className="bg-[#F9FAFB] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">Loading posts...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black/10">
              {displayPosts.map((post, i) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  data-testid={`blog-post-card-${i}`}
                  className={`block bg-white group ${i < displayPosts.length - 1 ? 'border-b md:border-b-0 md:border-r border-black/10' : ''}`}
                >
                  {post.cover_image && (
                    <img src={post.cover_image} alt={post.title} className="w-full h-48 object-cover" />
                  )}
                  <div className="p-6 md:p-8">
                    {post.category && (
                      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3 block">
                        {post.category}
                      </span>
                    )}
                    <h3 className="text-lg font-bold text-black mb-2 group-hover:text-[#002FA7] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">{post.excerpt}</p>
                    <span className="text-sm font-semibold text-[#002FA7] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read more <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {displayPosts.length === 0 && !loading && (
            <div className="text-center py-16 border border-black/10 bg-white">
              <p className="text-lg font-bold text-black mb-2">No posts yet</p>
              <p className="text-sm text-gray-600">Check back soon for new content.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
